import { useState, useEffect, createContext, useContext } from 'react';
import {
  DEFAULT_CONSENT, DEFAULT_LOCATION, CONSENT_CATEGORIES,
  CONSENT_STORAGE_KEY, LOCATION_STORAGE_KEY
} from './consentConstants';

const ConsentContext = createContext(null);

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
};

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => {
    try {
      const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.consentGiven === 'boolean') return parsed;
      }
    } catch (error) {
      console.warn('Error loading consent preferences:', error);
    }
    return DEFAULT_CONSENT;
  });

  const [location, setLocation] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.locationSet === 'boolean') return parsed;
      }
    } catch (error) {
      console.warn('Error loading location preferences:', error);
    }
    return DEFAULT_LOCATION;
  });

  const [showBanner, setShowBanner] = useState(() => {
    try {
      const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return !parsed.consentGiven;
      }
    } catch {
      // Show banner on error
    }
    return true;
  });
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent)); }
    catch (error) { console.warn('Error saving consent:', error); }
  }, [consent]);

  useEffect(() => {
    try { localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location)); }
    catch (error) { console.warn('Error saving location:', error); }
  }, [location]);

  const updateConsent = (category, value) => {
    if (category === 'necessary') return;
    setConsent(prev => ({ ...prev, [category]: value }));
  };

  const updateLocation = (updates) => {
    setLocation(prev => ({ ...prev, ...updates, locationSet: true }));
  };

  const clearLocation = () => setLocation(DEFAULT_LOCATION);

  const requestGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
            );
            const data = await response.json();
            const address = data.address || {};
            setLocation(prev => ({
              ...prev,
              city: address.city || address.town || address.village || null,
              parish: address.county?.replace(' Parish', '') || null,
              state: address.state || 'Louisiana',
              zipCode: address.postcode || null,
              allowGeolocation: true,
              locationSet: true
            }));
          } catch {
            setLocation(prev => ({ ...prev, allowGeolocation: true, locationSet: true }));
          }
        },
        () => { setLocation(prev => ({ ...prev, allowGeolocation: false })); }
      );
    }
  };

  const acceptAll = () => {
    setConsent({
      necessary: true, analytics: true, marketing: true,
      personalization: true, thirdParty: true,
      consentGiven: true, consentDate: new Date().toISOString(), consentVersion: '1.0'
    });
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptNecessaryOnly = () => {
    setConsent({ ...DEFAULT_CONSENT, consentGiven: true, consentDate: new Date().toISOString() });
    setShowBanner(false);
    setShowSettings(false);
  };

  const savePreferences = () => {
    setConsent(prev => ({ ...prev, consentGiven: true, consentDate: new Date().toISOString() }));
    setShowBanner(false);
    setShowSettings(false);
  };

  const revokeConsent = () => {
    setConsent(DEFAULT_CONSENT);
    setShowBanner(true);
  };

  const value = {
    consent, updateConsent, acceptAll, acceptNecessaryOnly, savePreferences, revokeConsent,
    showBanner, setShowBanner, showSettings, setShowSettings,
    categories: CONSENT_CATEGORIES,
    location, updateLocation, clearLocation, requestGeolocation
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}
